import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.browser.document
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import kotlin.js.Date

fun isTourSite(): Boolean {
    val host = window.location.hostname
    val path = window.location.pathname.trimEnd('/')
    return host.startsWith("tour.") ||
        (host in setOf("localhost", "127.0.0.1", "[::1]") && (path == "/tour" || path.startsWith("/tour/")))
}

internal fun tourBase(): String = if (window.location.hostname.startsWith("tour.")) "" else "/tour"
private fun tourSection(): String = window.location.pathname.removePrefix(tourBase()).trim('/')
internal val TOUR_DISCIPLINES = listOf("narubak" to "Narubák", "eights" to "Osmičky", "nines" to "Devítky", "classic" to "Klasika")
private val TOUR_SEASON_STATES = listOf("draft" to "Koncept — neveřejný", "active" to "Probíhá — veřejný", "finished" to "Ukončený — veřejný")
internal val TOUR_TOURNAMENT_STATES = listOf("draft" to "Koncept — neveřejný", "published" to "Plánovaný", "open" to "Přihlášky otevřené", "running" to "Probíhá", "finished" to "Ukončený")
internal fun tourLabel(value: String, labels: List<Pair<String, String>>) = labels.firstOrNull { it.first == value }?.second ?: value

@Composable
fun TourApp() {
    var token by remember { mutableStateOf(loadPersistedToken()) }
    var expired by remember { mutableStateOf(false) }
    var section by remember { mutableStateOf(tourSection()) }
    var navOpen by remember { mutableStateOf(false) }
    var pendingAuth by remember { mutableStateOf(parseAuthHashToken(window.location.hash)) }
    LaunchedEffect(token) { persistToken(token) }
    LaunchedEffect(Unit) {
        document.title = "Bowling Tour"
        document.querySelector("link[rel='icon']")?.setAttribute("href", "/tour-favicon.svg")
        if (pendingAuth != null) window.history.replaceState(null, "", window.location.pathname)
    }
    LaunchedEffect(token) {
        val exp = token?.let { decodeJwtClaims(it)?.exp } ?: return@LaunchedEffect
        val remaining = exp * 1000.0 - Date().getTime()
        if (remaining > 0) delay(remaining.toLong())
        token = null; expired = true
    }
    DisposableEffect(Unit) {
        val listener: (org.w3c.dom.events.Event) -> Unit = { section = tourSection(); navOpen = false }
        window.addEventListener("popstate", listener)
        onDispose { window.removeEventListener("popstate", listener) }
    }
    if (pendingAuth != null) {
        SetPasswordScreen(pendingAuth!!) { token = it; pendingAuth = null }
        return
    }
    val navigate: (String) -> Unit = {
        val path = tourBase() + if (it.isEmpty()) "/" else "/$it"
        if (window.location.pathname != path) window.history.pushState(null, "", path)
        section = it; navOpen = false
    }
    val admin = section == "admin" || section.startsWith("admin/")
    var permitted by remember(token) { mutableStateOf<Boolean?>(null) }
    var permissionError by remember(token) { mutableStateOf<String?>(null) }
    var permissionRetry by remember { mutableStateOf(0) }
    LaunchedEffect(token, admin, permissionRetry) {
        permitted = null; permissionError = null
        val access = token ?: return@LaunchedEffect
        if (!admin) return@LaunchedEffect
        try { permitted = TourApi.canManage(access) }
        catch (e: Throwable) {
            if (e is CancellationException) throw e
            permissionError = e.apiMessage()
        }
    }
    Div(attrs = { classes("app-shell", "tour-shell"); if (admin) classes("tour-admin-shell"); if (navOpen) classes("nav-open") }) {
        if (navOpen) Div(attrs = { classes("nav-backdrop"); onClick { navOpen = false } }) {}
        Div(attrs = { classes("sidebar") }) {
            Div(attrs = { classes("brand") }) {
                Div(attrs = { classes("brand-ball") }) {}
                Div { H1 { Text("TOUR") }; P { Text("Plzeňská bowling tour") } }
            }
            Div(attrs = { classes("nav") }) {
                NavButton("Aktuálně", !admin && section.isEmpty()) { navigate("") }
                NavButton("Handicapy", section == "handicapy") { navigate("handicapy") }
                NavButton("Celkové pořadí", section == "poradi") { navigate("poradi") }
                NavButton("VPP", section == "vpp") { navigate("vpp") }
                TourContentNavigation(section, navigate)
                Div(attrs = { classes("nav-divider") }) {}
                NavButton("Administrace Tour", admin) { navigate("admin") }
                A(href = if (window.location.hostname.startsWith("tour."))
                    "${window.location.protocol}//${window.location.hostname.removePrefix("tour.")}/" else "/",
                    attrs = { classes("nav-link") }) { Text("Bowling liga") }
            }
        }
        Div(attrs = { classes("content") }) {
            Div(attrs = { classes("topbar") }) {
                Button(attrs = { classes("menu-button"); attr("aria-label", "Otevřít navigaci"); onClick { navOpen = !navOpen } }) { Text("☰") }
                H2 { Text(if (admin) "Administrace Tour" else "Bowling Tour") }
                if (admin && token != null) {
                    Span { Text(decodeJwtClaims(token!!)?.email ?: "Přihlášený správce") }
                    Button(attrs = { onClick { token = null } }) { Text("Odhlásit") }
                }
            }
            Div(attrs = { classes("page"); if (admin) classes("tour-admin") }) {
                if (!admin && section == "handicapy") TourPublicHandicaps()
                else if (!admin && section == "poradi") TourStandings()
                else if (!admin && section == "vpp") TourPublicVpp()
                else if (!admin && section.startsWith("obsazovacka/")) TourPublicRegistration(section.removePrefix("obsazovacka/"))
                else if (!admin && section.startsWith("vysledky/")) TourPublicResults(section.removePrefix("vysledky/"))
                else if (!admin && section.startsWith("turnaj/")) TourPublicTournament(section.removePrefix("turnaj/"))
                else if (!admin && (section.startsWith("stranka/") || section == "propozice")) TourPublicContent(section.removePrefix("stranka/"))
                else if (!admin && section.isNotEmpty()) {
                    Div(attrs = { classes("card") }) { H2 { Text("Stránka nenalezena") } }
                } else if (!admin) TourPublicFoundation()
                else if (token == null) TourLogin(expired) { token = it; expired = false }
                else if (permissionError != null) {
                    Div(attrs = { classes("card") }) {
                        P(attrs = { classes("editor-error"); attr("role", "alert") }) { Text("Ověření oprávnění selhalo: $permissionError") }
                        Button(attrs = { onClick { permissionRetry++ } }) { Text("Ověřit znovu") }
                    }
                } else if (permitted == null) P { Text("Ověřuji oprávnění…") }
                else if (permitted != true) Div(attrs = { classes("card") }) {
                    H2 { Text("Nemáte oprávnění ke správě Tour") }
                    P { Text("Přístup přiděluje správce Tour. Obraťte se na pořadatele.") }
                } else key(token) {
                    Div(attrs = { classes("tour-tabs") }) {
                        listOf("admin" to "Turnaje", "admin/handicapy" to "Handicapy", "admin/stranky" to "Obsahové stránky", "admin/rocniky" to "Ročníky").forEach { (path, label) ->
                            Button(attrs = { if (section == path || (path == "admin" && (section == "admin/turnaje" || section.startsWith("admin/turnaj/") || section in listOf("admin/prubeh", "admin/obsazovacka", "admin/hry", "admin/vpp", "admin/hraci"))) || (path == "admin/rocniky" && section in listOf("admin/poradi", "admin/rocniky/poradi"))) { classes("primary"); attr("aria-current", "page") }; onClick { navigate(path) } }) { Text(label) }
                        }
                    }
                    LaunchedEffect(section) {
                        document.querySelector(".tour-tabs [aria-current='page']")?.asDynamic()?.scrollIntoView(js("({block: 'nearest', inline: 'nearest'})"))
                    }
                    when (section) {
                        "admin", "admin/turnaje", "admin/rocniky" -> key(section) { TourCompetitionAdmin(token!!, section != "admin/rocniky", navigate) }
                        "admin/stranky" -> TourContentAdmin(token!!)
                        "admin/hraci" -> TourPlayersAdmin(token!!)
                        "admin/handicapy" -> TourHandicapsAdmin(token!!)
                        "admin/obsazovacka" -> TourWorkflowEntry(token!!, "prihlasovani", navigate)
                        "admin/hry" -> TourWorkflowEntry(token!!, "kvalifikace", navigate)
                        "admin/poradi", "admin/rocniky/poradi" -> TourStandings(token!!)
                        "admin/vpp" -> TourWorkflowEntry(token!!, "vpp", navigate)
                        else -> if (section.startsWith("admin/turnaj/")) {
                            val parts = section.removePrefix("admin/turnaj/").split('/')
                            TourTournamentWorkflow(token!!, parts.first(), parts.getOrElse(1) { "prezentace" }, navigate)
                        } else P { Text("Stránka nenalezena.") }
                    }
                }
            }
        }
    }
}

@Composable
private fun TourLogin(expired: Boolean, onToken: (String) -> Unit) {
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var busy by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()
    Div(attrs = { classes("card") }) {
        H2 { Text("Přihlášení správce Tour") }
        if (expired) P { Text("Přihlášení vypršelo. Přihlaste se znovu.") }
        Form(attrs = { classes("tour-form"); onSubmit { event ->
            event.preventDefault()
            if (!busy) scope.launch {
                busy = true; error = null
                try {
                    val response = Api.login(email.trim(), password)
                    val access = response.accessToken
                    if (access != null) onToken(access) else error = response.errorDescription ?: "Přihlášení selhalo."
                } catch (e: Throwable) {
                    if (e is CancellationException) throw e
                    error = e.apiMessage()
                } finally { busy = false }
            }
        } }) {
            TourField("E-mail", email, busy, InputType.Email) { email = it }
            TourField("Heslo", password, busy, InputType.Password) { password = it }
            Button(attrs = { classes("primary"); type(ButtonType.Submit); if (busy) disabled() }) { Text(if (busy) "Přihlašuji…" else "Přihlásit") }
        }
        TourError(error)
    }
}

@Composable
internal fun TourField(label: String, text: String, busy: Boolean = false, inputType: InputType<String> = InputType.Text, required: Boolean = true, onChange: (String) -> Unit) {
    Label {
        Span { Text(label) }
        Input(type = inputType) {
            attr("aria-label", label); value(text)
            if (required) required()
            if (busy) disabled()
            onInput { onChange(it.value) }
        }
    }
}

@Composable
internal fun TourError(error: String?) {
    error?.let { P(attrs = { classes("editor-error"); attr("role", "alert") }) { Text(it) } }
}

@Composable
internal fun TourChoice(label: String, value: String, options: List<Pair<String, String>>, busy: Boolean = false, onChange: (String) -> Unit) {
    Label {
        Span { Text(label) }
        Select(attrs = { attr("aria-label", label); if (busy) disabled(); onChange { it.value?.let(onChange) } }) {
            options.forEach { (id, title) -> Option(id, attrs = { if (id == value) selected() }) { Text(title) } }
        }
    }
}

@Composable
private fun TourDescription(value: String, busy: Boolean, onChange: (String) -> Unit) {
    Label {
        Span { Text("Popis") }
        TextArea(value = value, attrs = { attr("aria-label", "Popis"); if (busy) disabled(); onInput { onChange(it.value) } })
    }
}

@Composable
private fun TourCompetitionAdmin(token: String, tournamentsTab: Boolean, navigate: (String) -> Unit) {
    var seasons by remember { mutableStateOf<List<TourSeasonDto>>(emptyList()) }
    var selectedSeason by remember { mutableStateOf("") }
    var tournaments by remember { mutableStateOf<List<TourTournamentDto>>(emptyList()) }
    var refresh by remember { mutableStateOf(0) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var creatingSeason by remember { mutableStateOf(false) }
    var editingSeason by remember { mutableStateOf<TourSeasonDto?>(null) }
    var editingTournament by remember { mutableStateOf<TourTournamentDto?>(null) }
    var transitioning by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()
    fun beginPresentation(t: TourTournamentDto) {
        if (transitioning != null) return
        scope.launch {
            transitioning = t.id; error = null
            try {
                TourOperationsApi.beginPresentation(token, t)
                navigate("admin/turnaj/${t.id}/prezentace")
            } catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
            finally { transitioning = null }
        }
    }
    LaunchedEffect(refresh) {
        loading = true; error = null
        try {
            seasons = TourApi.seasons(token)
            selectedSeason = currentTourSeason(seasons, admin = true)?.id ?: ""
        } catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
        finally { loading = false }
    }
    LaunchedEffect(selectedSeason, refresh) {
        tournaments = emptyList(); editingTournament = null
        if (selectedSeason.isNotEmpty() && tournamentsTab) {
            loading = true
            try { tournaments = TourApi.tournaments(token, selectedSeason) }
            catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
            finally { loading = false }
        }
    }
    Div(attrs = { classes("card") }) {
        H2 { Text(if (tournamentsTab) "Turnaje · ${seasons.firstOrNull { it.id == selectedSeason }?.name ?: "Tour"}" else "Ročníky Tour") }
        TourError(error)
        if (error != null) Button(attrs = { onClick { refresh++ } }) { Text("Načíst znovu") }
        if (loading) P { Text("Načítám…") }
        if (tournamentsTab) {
            if (!loading && selectedSeason.isEmpty()) P { Text("Nejprve založte nebo aktivujte aktuální ročník v záložce Ročníky.") }
            if (selectedSeason.isNotEmpty()) {
                Table(attrs = { classes("tour-admin-table") }) {
                    Tr { Th { Text("Turnaj") }; Th { Text("Datum") }; Th { Text("Disciplína") }; Th { Text("Stav") }; Th {} }
                    tournaments.forEach { t -> Tr {
                        Td(attrs = { classes("tour-player-name") }) { Text(t.title) }; Td { Text(t.startsOn?.let(::formatDate) ?: "Neurčeno") }
                        Td { Text(tourLabel(t.discipline, TOUR_DISCIPLINES)) }
                        Td { Text(tourLabel(t.status, TOUR_TOURNAMENT_STATES)) }
                        Td { Div(attrs = { classes("tour-table-actions") }) {
                            if (tourRegistrationOpen(t)) {
                                Button(attrs = { attr("aria-label", "Přihlašování: ${t.title}"); if (transitioning != null) disabled(); onClick { navigate("admin/turnaj/${t.id}/prihlasovani") } }) { Text("Přihlašování") }
                                Button(attrs = { classes("primary"); attr("aria-label", "Zahájit prezentaci: ${t.title}"); if (transitioning != null) disabled(); onClick { beginPresentation(t) } }) { Text(if (transitioning == t.id) "Zahajuji…" else "Zahájit prezentaci") }
                            } else if (t.presentationStarted || t.status in listOf("running", "finished")) {
                                Button(attrs = { classes("primary"); attr("aria-label", "Průběh turnaje: ${t.title}"); onClick { navigate("admin/turnaj/${t.id}/${tourWorkflowDefaultStep(t)}") } }) { Text(if (t.status == "finished") "Otevřít průběh" else "Pokračovat") }
                            }
                            Button(attrs = { onClick { editingTournament = t } }) { Text("Upravit") }
                        } }
                    } }
                }
            }
        } else {
            Div(attrs = { classes("tour-actions") }) {
                Button(attrs = { if (loading) disabled(); onClick { editingSeason = null; creatingSeason = true } }) { Text("Nový ročník") }
                if (selectedSeason.isNotEmpty()) Button(attrs = { onClick { navigate("admin/rocniky/poradi") } }) { Text("Celkové pořadí a uzavření ročníku") }
            }
            if (!loading && seasons.isEmpty()) P { Text("Zatím nejsou žádné ročníky Tour.") }
            Table(attrs = { classes("tour-admin-table") }) {
                Tr { Th { Text("Ročník") }; Th { Text("Začátek") }; Th { Text("Stav") }; Th {} }
                seasons.forEach { s -> Tr {
                    Td(attrs = { classes("tour-player-name") }) { Text(s.name) }; Td { Text(formatDate(s.startDate)) }
                    Td { Text(tourLabel(s.status, TOUR_SEASON_STATES)) }
                    Td { Button(attrs = { onClick { editingSeason = s } }) { Text("Upravit") } }
                } }
            }
        }
    }
    if (!tournamentsTab && (creatingSeason || editingSeason != null || (!loading && seasons.isEmpty()))) key(editingSeason) {
        TourSeasonForm(token, editingSeason, onSaved = { editingSeason = null; creatingSeason = false; refresh++ }, onCancel = { editingSeason = null; creatingSeason = false })
    } else editingTournament?.let { tournament -> key(tournament.id) {
        TourTournamentForm(token, tournament, onSaved = { editingTournament = null; refresh++ }, onCancel = { editingTournament = null })
    } }
}

@Composable
private fun TourSeasonForm(token: String, editing: TourSeasonDto?, onSaved: () -> Unit, onCancel: () -> Unit) {
    if (editing?.status == "finished") {
        Div(attrs = { classes("card") }) {
            P { Text("Uzavřený ročník již nelze upravovat. Výsledky jsou dostupné v záložce Celkové pořadí.") }
            Button(attrs = { onClick { onCancel() } }) { Text("Zavřít detail ročníku") }
        }
        return
    }
    var name by remember { mutableStateOf(editing?.name ?: "") }
    var start by remember { mutableStateOf(editing?.startDate ?: "") }
    var end by remember { mutableStateOf(editing?.endDate ?: "") }
    var status by remember { mutableStateOf(editing?.status ?: "draft") }
    var description by remember { mutableStateOf(editing?.description ?: "") }
    var schedule by remember { mutableStateOf(List(8) { TourScheduledTournament(TOUR_DISCIPLINES[it % 4].first) }) }
    val requestId = remember(name, start, end, status, description, schedule) { window.asDynamic().crypto.randomUUID() as String }
    val validSchedule = TOUR_DISCIPLINES.all { (discipline, _) -> schedule.count { it.discipline == discipline } == 2 }
    var busy by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()
    Div(attrs = { classes("card") }) {
        H2 { Text(if (editing == null) "Nový ročník" else "Upravit ročník") }
        Form(attrs = { classes("tour-form"); onSubmit { event ->
            event.preventDefault()
            if (!busy) scope.launch {
                busy = true; error = null
                try {
                    val input = TourSeasonInput(name.trim(), start, end.ifBlank { null }, status, description)
                    if (editing == null) TourApi.createSeason(token, requestId, input, schedule)
                    else TourApi.saveSeason(token, editing.id, input)
                    name = ""; start = ""; end = ""; status = "draft"; description = ""
                    schedule = List(8) { TourScheduledTournament(TOUR_DISCIPLINES[it % 4].first) }
                    onSaved()
                } catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
                finally { busy = false }
            }
        } }) {
            TourField("Název ročníku", name, busy) { name = it }
            TourField("Začátek ročníku", start, busy, InputType.Date) { start = it }
            TourField("Konec ročníku", end, busy, InputType.Date, required = false) { end = it }
            TourChoice("Stav ročníku", status, TOUR_SEASON_STATES.filter { it.first != "finished" || status == "finished" }, busy || status == "finished") { status = it }
            P(attrs = { classes("muted") }) { Text("Po dokončení turnajů uzavřete ročník přes Celkové pořadí a uzavření ročníku v záložce Ročníky.") }
            P(attrs = { classes("muted") }) { Text("Koncept je neveřejný. Změnou na Probíhá nebo Ukončený zveřejníte ročník a jeho zveřejněné turnaje.") }
            TourDescription(description, busy) { description = it }
            if (editing == null) {
                H4 { Text("Osm turnajů ročníku") }
                P { Text("Dva turnaje od každé disciplíny. Nastavte jejich pořadí a termíny; termíny lze později upravit v záložce Turnaje.") }
                schedule.forEachIndexed { index, row -> Div(attrs = { classes("tour-round-settings") }) {
                    TourChoice("Disciplína ${index+1}. turnaje", row.discipline, TOUR_DISCIPLINES, busy) { value ->
                        schedule = schedule.mapIndexed { i, item -> if (i == index) item.copy(discipline = value) else item }
                    }
                    TourField("Termín ${index+1}. turnaje", row.startsOn ?: "", busy, InputType.Date, required = false) { value ->
                        schedule = schedule.mapIndexed { i, item -> if (i == index) item.copy(startsOn = value.ifBlank { null }) else item }
                    }
                } }
                if (!validSchedule) P(attrs = { classes("editor-error") }) { Text("Každá disciplína musí být zvolena právě dvakrát.") }
                P { Text("Turnaje s termínem budou zveřejněné se zavřenými přihláškami, bez termínu zůstanou koncepty. Ročník v konceptu je celý neveřejný.") }
            }
            Button(attrs = { classes("primary"); type(ButtonType.Submit); if (busy || (editing == null && !validSchedule)) disabled() }) { Text(if (busy) "Ukládám…" else "Uložit ročník") }
            if (editing != null) Button(attrs = { type(ButtonType.Button); if (busy) disabled(); onClick { onCancel() } }) { Text("Zrušit úpravu") }
        }
        TourError(error)
    }
}

@Composable
private fun TourTournamentForm(token: String, editing: TourTournamentDto, onSaved: () -> Unit, onCancel: () -> Unit) {
    var date by remember { mutableStateOf(editing.startsOn ?: "") }
    var status by remember { mutableStateOf(editing.status) }
    var busy by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()
    val started = editing.status in listOf("running", "finished")
    Div(attrs = { classes("card") }) {
        H2 { Text("Upravit turnaj") }
        P { Text(editing.title) }
        Form(attrs = { classes("tour-form"); onSubmit { event ->
            event.preventDefault()
            if (!busy) scope.launch {
                busy = true; error = null
                try {
                    TourApi.saveTournament(token, editing, date.ifBlank { null }, status)
                    onSaved()
                } catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
                finally { busy = false }
            }
        } }) {
            TourField("Datum turnaje", date, busy || started, InputType.Date, required = status != "draft") { date = it }
            TourChoice("Stav turnaje", status, TOUR_TOURNAMENT_STATES.filter { if (started) it.first == editing.status else it.first in listOf("draft", "published", "open") }, busy || started) { status = it }
            P(attrs = { classes("muted") }) { Text("Plánovaný turnaj má přihlášky zavřené. Otevřete je změnou stavu krátce před turnajem.") }
            P(attrs = { classes("muted") }) { Text("Turnaj uzavřete zveřejněním výsledků v jeho průběhu, v kroku Finále a uzavření.") }
            if (started) P { Text("Termín zahájeného turnaje je uzamčený.") }
            Button(attrs = { classes("primary"); type(ButtonType.Submit); if (busy || editing.status == "finished") disabled() }) { Text(if (busy) "Ukládám…" else "Uložit turnaj") }
            Button(attrs = { type(ButtonType.Button); if (busy) disabled(); onClick { onCancel() } }) { Text("Zrušit úpravu") }
        }
        TourError(error)
    }
}
@Composable
internal fun TourPlayersAdmin(token: String, onCreated: () -> Unit = {}) {
    var players by remember { mutableStateOf<List<TourPlayerDto>>(emptyList()) }
    var search by remember { mutableStateOf("") }
    var name by remember { mutableStateOf("") }
    var fullName by remember { mutableStateOf("") }
    var gender by remember { mutableStateOf("male") }
    var showGuest by remember { mutableStateOf(false) }
    var guestId by remember { mutableStateOf(window.asDynamic().crypto.randomUUID() as String) }
    var loading by remember { mutableStateOf(true) }
    var busy by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var refresh by remember { mutableStateOf(0) }
    val scope = rememberCoroutineScope()
    LaunchedEffect(refresh) {
        loading = true
        try { players = TourApi.players(token); error = null }
        catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
        finally { loading = false }
    }
    Div(attrs = { classes("card") }) {
        H2 { Text("Společná evidence hráčů") }
        Div(attrs = { classes("tour-admin-toolbar") }) {
            TourField("Hledat hráče", search, required = false) { search = it }
            Button(attrs = { if (busy) disabled(); attr("aria-expanded", showGuest.toString()); onClick { showGuest = !showGuest } }) { Text(if (showGuest) "Zavřít nového hosta" else "Nový host") }
        }
        if (loading) P { Text("Načítám…") }
        TourError(error)
        if (error != null) Button(attrs = { onClick { refresh++ } }) { Text("Načíst znovu") }
        if (showGuest) Div(attrs = { classes("tour-admin-inline-form") }) {
            H2 { Text("Nový host") }
            P { Text("Nejprve vyhledejte hráče v evidenci. Nový host bude dostupný i v dalších turnajích.") }
            Form(attrs = { classes("tour-form"); onSubmit { event ->
                event.preventDefault()
                if (!busy) scope.launch {
                    busy = true; error = null
                    try {
                        TourApi.createGuest(token, guestId, name.trim(), fullName.trim(), gender)
                        name = ""; fullName = ""; guestId = window.asDynamic().crypto.randomUUID() as String
                        showGuest = false; refresh++; onCreated()
                    } catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
                    finally { busy = false }
                }
            } }) {
                TourField("Zobrazované jméno", name, busy) { name = it }
                TourField("Celé jméno", fullName, busy) { fullName = it }
                TourChoice("Pohlaví", gender, listOf("male" to "Muž", "female" to "Žena"), busy) { gender = it }
                Button(attrs = { classes("primary"); type(ButtonType.Submit); if (busy || loading) disabled() }) { Text(if (busy) "Ukládám…" else "Založit hosta") }
            }
        }
        Table(attrs = { classes("tour-admin-table") }) {
            Tr { Th { Text("Hráč") }; Th { Text("Celé jméno") }; Th { Text("Pohlaví") } }
            players.filter { search.isBlank() || "${it.name} ${it.fullName.orEmpty()}".contains(search, ignoreCase = true) }.forEach { player ->
                Tr { Td(attrs = { classes("tour-player-name") }) { Text(player.name) }; Td { Text(player.fullName ?: "—") }; Td { Text(genderLabel(player.gender)) } }
            }
        }
    }

}

@Composable
private fun TourPublicFoundation() {
    TourSeasonScope(null, showSeason = false, includeFinished = true) { season, tournaments ->
        Div(attrs = { classes("hero", "tour-hero"); attr("aria-labelledby", "tour-hero-title") }) {
            Div(attrs = { classes("hero-text") }) {
                H1(attrs = { id("tour-hero-title") }) { Text("Plzeňská bowling tour") }
                P(attrs = { classes("hero-tagline") }) { Text(season.name) }
                if (season.description.isNotBlank()) P { Text(season.description) }
            }
            val today = Date().let { "${it.getFullYear()}-${(it.getMonth()+1).toString().padStart(2,'0')}-${it.getDate().toString().padStart(2,'0')}" }
            val next = tournaments.firstOrNull { it.status == "running" }
                ?: tournaments.filter { it.startsOn != null && it.startsOn >= today && it.status in listOf("published", "open") }.minByOrNull { it.startsOn!! }
            val last = tournaments.filter { it.status == "finished" }.maxByOrNull { it.startsOn.orEmpty() }
            if (next != null || last != null) Div(attrs = { classes("tour-highlights") }) {
                next?.let { t -> Div {
                    H4 { Text(if (t.status == "running") "Právě se hraje" else "Nejbližší turnaj") }
                    A(href = tourBase() + "/turnaj/" + t.slug) { Text("${t.title} · ${t.startsOn?.let(::formatDate) ?: "Termín bude upřesněn"}") }
                    P { Text(if (t.status == "open") "Přihlášky jsou otevřené." else if (t.status == "running") "Sledujte průběžné výsledky." else "Přihlášky zatím nejsou otevřené.") }
                } }
                last?.let { t -> Div {
                    H4 { Text("Poslední výsledky") }
                    A(href = tourBase() + "/vysledky/" + t.slug) { Text("${t.title} · ${t.startsOn?.let(::formatDate).orEmpty()}") }
                    P { A(href = tourBase() + "/poradi") { Text("Celkové pořadí Tour →") } }
                } }
            }
        }
        Div(attrs = { classes("card", "tour-tournaments-section"); attr("aria-labelledby", "tour-tournaments-title") }) {
            H2(attrs = { id("tour-tournaments-title") }) { Text("Turnaje") }
            if (tournaments.isEmpty()) P { Text("Zatím nejsou zveřejněné žádné turnaje.") }
            if (tournaments.isNotEmpty()) Div(attrs = { classes("tour-tournament-scroll") }) {
                Table(attrs = { classes("tour-tournament-table"); attr("aria-label", "Turnaje Tour") }) {
                    Thead { Tr {
                        Th { Text("Termín") }; Th { Text("Turnaj") }; Th { Text("Stav") }
                        Th { Span(attrs = { classes("tour-visually-hidden") }) { Text("Detail turnaje") } }
                    } }
                    Tbody { tournaments.forEach { t -> Tr {
                        Td(attrs = { classes("tour-tournament-date") }) { Text(t.startsOn?.let(::formatDate) ?: "Bude upřesněn") }
                        Td {
                            A(href = tourBase() + "/turnaj/" + t.slug, attrs = { classes("tour-tournament-title") }) { Text(t.title) }
                            if (t.description.isNotBlank()) P(attrs = { classes("tour-tournament-note") }) { Text(t.description) }
                        }
                        Td { Span(attrs = { classes("tour-status", "tour-status-${t.status}") }) {
                            Text(when (t.status) {
                                "published" -> "Plánovaný"
                                "open" -> "Přihlášky otevřené"
                                else -> tourLabel(t.status, TOUR_TOURNAMENT_STATES)
                            })
                        } }
                        Td { if (t.status == "open") A(href = tourBase() + "/obsazovacka/" + t.slug,
                            attrs = { classes("tour-registration-link") }) { Text("Obsazovačka a rozlosování →") }
                            else if (t.status in listOf("running", "finished")) A(href = tourBase() + "/vysledky/" + t.slug,
                                attrs = { classes("tour-registration-link") }) { Text("Výsledky →") }
                        }
                    } } }
                }
            }
        }
    }
}
